import { createFileRoute, Link } from "@tanstack/react-router";
import { requireAdmin } from "@/lib/admin-guard";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import {
  listBroadcasts,
  deleteBroadcast,
  getBroadcastStats,
  getBroadcastSendSettings,
  updateBroadcastSendSettings,
} from "@/lib/broadcasts.functions";
import {
  sendActivationReminderBroadcast,
  getActivationReminderStatus,
  retryFailedActivationReminder,
} from "@/lib/activation-reminder.functions";
import {
  Mail,
  Plus,
  ArrowLeft,
  Users,
  Heart,
  Stethoscope,
  UserCircle2,
  Send,
  TrendingUp,
  MoreVertical,
  Pencil,
  Copy,
  Trash2,
  Sparkles,
  Calendar,
  Clock,
  FileText,
  Bot,
  PartyPopper,
  BarChart3,
  Layers,
  Lock,
} from "lucide-react";
import { toast } from "sonner";
import { useState, useRef, useEffect } from "react";

export const Route = createFileRoute("/admin_/broadcasts")({
  ssr: false,
  head: () => ({
    meta: [
      { title: "Email Broadcasts — Admin" },
      { name: "robots", content: "noindex,nofollow" },
    ],
  }),
  beforeLoad: requireAdmin,
  component: BroadcastsList,
});

const AUDIENCE_LABELS: Record<string, string> = {
  everyone: "Everyone",
  waitlist: "Waitlist",
  therapist_applicants: "Therapists",
  approved_therapists: "Approved therapists",
  members: "Members Only",
  founding_members: "Founding members (paid)",
  everyone_except_founding_members: "Everyone except Founding Members",
  not_joined: "Not joined yet",
  waitlist_unpaid: "Waitlist (unpaid)",
  marketing_opt_in: "Marketing opt-in",
  registered_no_active_paid: "Non-Members Only",
  cold_audience: "Cold audience",
  cold_members: "Cold members (past buyers, never signed up)",
  cancelled_members: "Cancelled members",
  new_trial: "New trial",
  new: "New (waitlist, emailed & opened)",
  never_paid: "Never paid (members + waiting list)",
};


const STATUS_STYLES: Record<string, { dot: string; pill: string; label: string }> = {
  draft: {
    dot: "bg-muted-foreground",
    pill: "bg-muted text-muted-foreground border-border",
    label: "Draft",
  },
  scheduled: {
    dot: "bg-blue-500",
    pill: "bg-blue-50 text-blue-700 border-blue-200",
    label: "Scheduled",
  },
  sending: {
    dot: "bg-amber-500",
    pill: "bg-amber-50 text-amber-700 border-amber-200",
    label: "Sending",
  },
  sent: {
    dot: "bg-emerald-500",
    pill: "bg-emerald-50 text-emerald-700 border-emerald-200",
    label: "Sent",
  },
  failed: {
    dot: "bg-red-500",
    pill: "bg-red-50 text-red-700 border-red-200",
    label: "Failed",
  },
};

function formatNumber(n: number) {
  return new Intl.NumberFormat("en-GB").format(n);
}

function formatPct(n: number) {
  return `${(n * 100).toFixed(1)}%`;
}

function BroadcastsList() {
  const listFn = useServerFn(listBroadcasts);
  const statsFn = useServerFn(getBroadcastStats);
  const delFn = useServerFn(deleteBroadcast);
  const qc = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: ["broadcasts"],
    queryFn: () => listFn(),
  });
  const { data: stats } = useQuery({
    queryKey: ["broadcasts", "stats"],
    queryFn: () => statsFn(),
  });

  const del = useMutation({
    mutationFn: (id: string) => delFn({ data: { id } }),
    onSuccess: () => {
      toast.success("Broadcast deleted");
      qc.invalidateQueries({ queryKey: ["broadcasts"] });
      qc.invalidateQueries({ queryKey: ["broadcasts", "stats"] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const activationFn = useServerFn(sendActivationReminderBroadcast);
  const activationStatusFn = useServerFn(getActivationReminderStatus);
  const activationRetryFn = useServerFn(retryFailedActivationReminder);

  const { data: activationStatus, refetch: refetchActivationStatus } = useQuery({
    queryKey: ["activation-reminder", "status"],
    queryFn: () => activationStatusFn(),
    refetchInterval: 10_000,
  });

  const activation = useMutation({
    mutationFn: () => activationFn(),
    onSuccess: (r) => {
      toast.success(
        `Activation reminder sent — ${r.sent}/${r.total} delivered${
          r.failed ? `, ${r.failed} failed` : ""
        }.`,
      );
      qc.invalidateQueries({ queryKey: ["broadcasts"] });
      qc.invalidateQueries({ queryKey: ["broadcasts", "stats"] });
      refetchActivationStatus();
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const activationRetry = useMutation({
    mutationFn: () => activationRetryFn(),
    onSuccess: (r) => {
      if (r.retried === 0) {
        toast.success("No failed recipients to retry — you're all caught up.");
      } else {
        toast.success(
          `Retried ${r.retried} — ${r.retry_sent} delivered${
            r.retry_failed ? `, ${r.retry_failed} still failing` : ""
          }.`,
        );
      }
      qc.invalidateQueries({ queryKey: ["broadcasts"] });
      qc.invalidateQueries({ queryKey: ["broadcasts", "stats"] });
      refetchActivationStatus();
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const broadcasts = data?.broadcasts ?? [];

  return (
    <div className="mx-auto max-w-6xl px-4 py-8 sm:py-10">
      {/* Top nav */}
      <Link
        to="/admin"
        className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
      >
        <ArrowLeft className="h-4 w-4" /> Admin Dashboard
      </Link>

      {/* Header */}
      <header className="mt-4 rounded-3xl border border-gold/30 bg-gradient-to-br from-cream/70 via-background to-cream/40 p-6 shadow-[var(--shadow-gold)] sm:p-8">
        <div className="flex items-center gap-2 text-xs font-medium uppercase tracking-wider text-[var(--gold-deep)]">
          <Sparkles className="h-3.5 w-3.5" /> Recovery Trauma™ Comms
        </div>
        <h1 className="mt-2 font-display text-2xl tracking-tight text-foreground sm:text-3xl">
          📧 Email Broadcasts
        </h1>
        <p className="mt-2 max-w-2xl text-sm leading-relaxed text-foreground/75 sm:text-base">
          Create and send beautiful emails to your community.
        </p>
        <div className="mt-4 flex flex-wrap gap-2 text-xs">
          {["Waitlist", "Members", "Therapists", "Beta Testers", "Newsletter Subscribers"].map(
            (a) => (
              <span
                key={a}
                className="rounded-full border border-gold/30 bg-cream/60 px-3 py-1 font-medium text-foreground/75"
              >
                {a}
              </span>
            ),
          )}
        </div>
      </header>

      <SendThrottlePanel />


      {/* Stats */}
      <section className="mt-6 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
        <StatCard
          icon={<Users className="h-4 w-4" />}
          label="Total Subscribers"
          value={stats ? formatNumber(stats.totalSubscribers) : "—"}
          emoji="📨"
        />
        <StatCard
          icon={<Heart className="h-4 w-4" />}
          label="Waitlist"
          value={stats ? formatNumber(stats.waitlist) : "—"}
          emoji="❤️"
        />
        <StatCard
          icon={<Stethoscope className="h-4 w-4" />}
          label="Therapists"
          value={stats ? formatNumber(stats.therapists) : "—"}
          emoji="👩‍⚕️"
        />
        <StatCard
          icon={<UserCircle2 className="h-4 w-4" />}
          label="Members"
          value={stats ? formatNumber(stats.members) : "—"}
          emoji="👥"
        />
        <StatCard
          icon={<Send className="h-4 w-4" />}
          label="Emails Sent"
          value={stats ? formatNumber(stats.emailsSent) : "—"}
          emoji="📩"
        />
        <StatCard
          icon={<TrendingUp className="h-4 w-4" />}
          label="Avg Open Rate"
          value={stats ? formatPct(stats.avgOpenRate) : "—"}
          emoji="📈"
        />
      </section>

      {/* Primary CTA */}
      <Link
        to="/admin/broadcasts/$id"
        params={{ id: "new" }}
        className="mt-6 flex w-full items-center justify-center gap-2 rounded-2xl bg-gradient-to-r from-[var(--gold-deep)] to-gold px-6 py-4 text-base font-semibold text-cream shadow-[var(--shadow-gold)] transition-all duration-200 hover:-translate-y-0.5 hover:shadow-lg active:scale-[0.99] sm:text-lg"
      >
        <Sparkles className="h-5 w-5" /> Create New Email Campaign
      </Link>

      {/* One-click launch reminder + live delivery timeline */}
      <div className="mt-4 rounded-2xl border border-gold/40 bg-cream/50 p-4 sm:p-5">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
          <div className="min-w-0">
            <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-[var(--gold-deep)]">
              <Send className="h-3.5 w-3.5" /> Launch reminder
            </div>
            <p className="mt-1 text-sm font-medium text-foreground">
              Send the "Activate your Membership Before Launch" email
            </p>
            <p className="mt-0.5 text-xs text-muted-foreground">
              Goes to registered users without an active paid membership.
              Active members, admins, unsubscribers, and already-sent recipients
              are excluded automatically. Safe to re-run — it only picks up new
              and previously failed addresses. Every run is written to the
              audit log.
            </p>
          </div>
          <div className="flex shrink-0 flex-col gap-2 sm:items-end">
            <button
              type="button"
              onClick={() => {
                if (
                  confirm(
                    "Send the launch payment reminder to all registered users without an active membership?",
                  )
                ) {
                  activation.mutate();
                }
              }}
              disabled={activation.isPending || activationRetry.isPending}
              className="inline-flex items-center justify-center gap-2 rounded-xl bg-foreground px-4 py-2.5 text-sm font-semibold text-background transition hover:opacity-90 disabled:opacity-60"
            >
              <Send className="h-4 w-4" />
              {activation.isPending ? "Sending…" : "Send payment reminder"}
            </button>
            {activationStatus?.exists && (activationStatus.failed ?? 0) > 0 && (
              <button
                type="button"
                onClick={() => {
                  if (confirm(`Retry ${activationStatus.failed} failed recipient(s)?`)) {
                    activationRetry.mutate();
                  }
                }}
                disabled={activation.isPending || activationRetry.isPending}
                className="inline-flex items-center justify-center gap-2 rounded-xl border border-border bg-background px-4 py-2 text-sm font-medium text-foreground transition hover:bg-muted disabled:opacity-60"
              >
                {activationRetry.isPending
                  ? "Retrying…"
                  : `Retry ${activationStatus.failed} failed`}
              </button>
            )}
          </div>
        </div>

        {activationStatus?.exists && (
          <div className="mt-4 space-y-3">
            {/* Timeline: Queued → Sent → Failed */}
            <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
              <TimelineTile label="Total" value={activationStatus.total} tone="neutral" />
              <TimelineTile label="Queued" value={activationStatus.queued} tone="queued" />
              <TimelineTile label="Sent" value={activationStatus.sent} tone="sent" />
              <TimelineTile label="Failed" value={activationStatus.failed} tone="failed" />
            </div>

            <div className="flex flex-wrap items-center justify-between gap-2 text-xs text-muted-foreground">
              <div>
                {activationStatus.last_run ? (
                  <>
                    Last run{" "}
                    <span className="text-foreground">
                      {new Date(activationStatus.last_run.at).toLocaleString()}
                    </span>{" "}
                    · <span className="capitalize">{activationStatus.last_run.status}</span>
                    {activationStatus.last_run.error ? (
                      <span className="text-destructive">
                        {" "}
                        · {activationStatus.last_run.error}
                      </span>
                    ) : null}
                  </>
                ) : (
                  "No runs recorded yet"
                )}
              </div>
              {activationStatus.broadcast_id && (
                <Link
                  to="/admin/broadcasts/$id/delivery"
                  params={{ id: activationStatus.broadcast_id }}
                  className="inline-flex items-center gap-1 rounded-lg border border-border bg-background px-2.5 py-1 font-medium text-foreground hover:bg-muted"
                >
                  <BarChart3 className="h-3.5 w-3.5" /> View delivery timeline
                </Link>
              )}
            </div>
          </div>
        )}
      </div>




      {/* Campaign list */}
      <section className="mt-8">
        <div className="mb-3 flex items-end justify-between">
          <h2 className="font-display text-xl text-foreground sm:text-2xl">Your campaigns</h2>
          {broadcasts.length > 0 && (
            <span className="text-xs text-muted-foreground">
              {broadcasts.length} {broadcasts.length === 1 ? "campaign" : "campaigns"}
            </span>
          )}
        </div>

        {isLoading ? (
          <div className="grid gap-3">
            {[0, 1, 2].map((i) => (
              <div
                key={i}
                className="h-28 animate-pulse rounded-2xl border border-border bg-card"
              />
            ))}
          </div>
        ) : broadcasts.length === 0 ? (
          <EmptyState />
        ) : (
          <ul className="grid gap-3">
            {broadcasts.map((b) => (
              <CampaignCard
                key={b.id}
                b={b}
                onDelete={(id) => {
                  if (confirm(`Delete broadcast "${b.subject}"?`)) del.mutate(id);
                }}
              />
            ))}
          </ul>
        )}
      </section>

      {/* Coming soon */}
      <section className="mt-12">
        <h2 className="font-display text-xl text-foreground sm:text-2xl">Coming soon</h2>
        <p className="mt-1 text-sm text-muted-foreground">
          We're building these next — reserved space in your Comms suite.
        </p>
        <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-3">
          <ComingSoonTile icon={<FileText className="h-5 w-5" />} label="Email Templates" />
          <ComingSoonTile icon={<Calendar className="h-5 w-5" />} label="Scheduled Campaigns" />
          <ComingSoonTile icon={<Bot className="h-5 w-5" />} label="Automations" />
          <ComingSoonTile icon={<PartyPopper className="h-5 w-5" />} label="Welcome Email" />
          <ComingSoonTile icon={<BarChart3 className="h-5 w-5" />} label="Newsletter Analytics" />
          <ComingSoonTile icon={<Layers className="h-5 w-5" />} label="Audience Segments" />
        </div>
      </section>
    </div>
  );
}

function StatCard({
  icon,
  label,
  value,
  emoji,
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
  emoji: string;
}) {
  return (
    <div className="group rounded-2xl border border-gold/25 bg-card p-4 shadow-sm transition-all duration-200 hover:-translate-y-0.5 hover:border-gold/50 hover:shadow-[var(--shadow-gold)]">
      <div className="flex items-center justify-between">
        <span className="text-lg">{emoji}</span>
        <span className="text-[var(--gold-deep)] opacity-70">{icon}</span>
      </div>
      <div className="mt-2 font-display text-xl tabular-nums text-foreground sm:text-2xl">
        {value}
      </div>
      <div className="mt-0.5 text-xs text-muted-foreground">{label}</div>
    </div>
  );
}

type Broadcast = {
  id: string;
  subject: string;
  audience: string;
  status: string;
  scheduled_for: string | null;
  sent_at: string | null;
  recipient_count: number | null;
  sent_count: number;
  failed_count: number;
  pending_count?: number;
  open_count: number;
  click_count: number;
  unsubscribe_count: number;
  last_error?: string | null;
  created_at: string;
};

function CampaignCard({
  b,
  onDelete,
}: {
  b: Broadcast;
  onDelete: (id: string) => void;
}) {
  const status = STATUS_STYLES[b.status] ?? STATUS_STYLES.draft;
  const openRate = b.sent_count > 0 ? b.open_count / b.sent_count : 0;
  const clickRate = b.sent_count > 0 ? b.click_count / b.sent_count : 0;
  const dateLabel = b.sent_at
    ? `Sent ${new Date(b.sent_at).toLocaleDateString()}`
    : b.scheduled_for
      ? `Scheduled ${new Date(b.scheduled_for).toLocaleDateString()}`
      : `Created ${new Date(b.created_at).toLocaleDateString()}`;
  const canDelete =
    b.status === "draft" || b.status === "failed" || b.status === "scheduled";

  return (
    <li className="group relative rounded-2xl border border-border bg-card p-4 shadow-sm transition-all duration-200 hover:-translate-y-0.5 hover:border-gold/40 hover:shadow-[var(--shadow-gold)] sm:p-5">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <Link
            to="/admin/broadcasts/$id"
            params={{ id: b.id }}
            className="block truncate font-display text-base font-semibold text-foreground hover:text-[var(--gold-deep)] sm:text-lg"
          >
            {b.subject}
          </Link>
          <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground">
            <span className="inline-flex items-center gap-1">
              <Users className="h-3 w-3" />
              {AUDIENCE_LABELS[b.audience] ?? b.audience}
            </span>
            <span className="inline-flex items-center gap-1">
              <Clock className="h-3 w-3" />
              {dateLabel}
            </span>
          </div>
        </div>
        <span
          className={`inline-flex min-h-10 sm:min-h-0 items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-medium ${status.pill}`}
        >
          <span className={`h-1.5 w-1.5 rounded-full ${status.dot}`} />
          {status.label}
        </span>
      </div>

      <div className="mt-4 grid grid-cols-3 gap-2 border-t border-border/60 pt-3">
        <Metric
          label="Delivered"
          value={
            b.recipient_count
              ? `${formatNumber(b.sent_count)}/${formatNumber(b.recipient_count)}`
              : formatNumber(b.sent_count)
          }
        />
        <Metric label="Opens" value={b.sent_count ? formatPct(openRate) : "—"} />
        <Metric label="Clicks" value={b.sent_count ? formatPct(clickRate) : "—"} />
      </div>

      {(b.failed_count > 0 || (b.pending_count ?? 0) > 0 || b.unsubscribe_count > 0) && (
        <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground">
          {b.failed_count > 0 && (
            <span className="text-destructive">{formatNumber(b.failed_count)} failed</span>
          )}
          {(b.pending_count ?? 0) > 0 && (
            <span>{formatNumber(b.pending_count ?? 0)} still queued</span>
          )}
          {b.unsubscribe_count > 0 && (
            <span>{formatNumber(b.unsubscribe_count)} unsubscribed</span>
          )}
        </div>
      )}

      {b.last_error && b.status === "failed" && (
        <p className="mt-2 line-clamp-2 rounded-lg bg-destructive/10 px-2 py-1 text-xs text-destructive">
          {b.last_error}
        </p>
      )}



      <CardMenu
        broadcastId={b.id}
        canDelete={canDelete}
        onDelete={() => onDelete(b.id)}
      />
    </li>
  );
}

function Metric({ label, value }: { label: string; value: string }) {
  return (
    <div className="text-center">
      <div className="font-display text-sm tabular-nums text-foreground sm:text-base">
        {value}
      </div>
      <div className="text-[10px] uppercase tracking-wider text-muted-foreground sm:text-xs">
        {label}
      </div>
    </div>
  );
}

function CardMenu({
  broadcastId,
  canDelete,
  onDelete,
}: {
  broadcastId: string;
  canDelete: boolean;
  onDelete: () => void;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [open]);

  return (
    <div ref={ref} className="absolute end-3 top-3">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-label="Campaign options"
        className="rounded-full p-2 text-muted-foreground transition-colors hover:bg-cream/60 hover:text-foreground"
      >
        <MoreVertical className="h-4 w-4" />
      </button>
      {open && (
        <div className="absolute end-0 z-10 mt-1 w-40 overflow-hidden rounded-xl border border-border bg-popover shadow-lg">
          <Link
            to="/admin/broadcasts/$id"
            params={{ id: broadcastId }}
            className="flex w-full items-center gap-2 px-3 py-2 text-sm text-foreground hover:bg-muted"
            onClick={() => setOpen(false)}
          >
            <Pencil className="h-3.5 w-3.5" /> Edit
          </Link>
          <button
            type="button"
            disabled
            className="flex w-full cursor-not-allowed items-center gap-2 px-3 py-2 text-sm text-muted-foreground"
            title="Coming soon"
          >
            <Copy className="h-3.5 w-3.5" /> Duplicate
          </button>
          <button
            type="button"
            onClick={() => {
              setOpen(false);
              if (canDelete) onDelete();
              else toast.error("Sent campaigns can't be deleted");
            }}
            className={`flex w-full items-center gap-2 px-3 py-2 text-sm ${
              canDelete
                ? "text-red-600 hover:bg-red-50"
                : "cursor-not-allowed text-muted-foreground"
            }`}
          >
            <Trash2 className="h-3.5 w-3.5" /> Delete
          </button>
        </div>
      )}
    </div>
  );
}

function TimelineTile({
  label,
  value,
  tone,
}: {
  label: string;
  value: number;
  tone: "neutral" | "queued" | "sent" | "failed";
}) {
  const toneClass =
    tone === "sent"
      ? "border-emerald-300/60 bg-emerald-50 text-emerald-900"
      : tone === "failed"
        ? "border-red-300/60 bg-red-50 text-red-900"
        : tone === "queued"
          ? "border-amber-300/60 bg-amber-50 text-amber-900"
          : "border-border bg-background text-foreground";
  return (
    <div className={`rounded-xl border px-3 py-2 ${toneClass}`}>
      <div className="text-[10px] font-semibold uppercase tracking-wider opacity-70">
        {label}
      </div>
      <div className="mt-0.5 text-lg font-semibold tabular-nums">{value}</div>
    </div>
  );
}

function EmptyState() {
  return (
    <div className="rounded-3xl border border-dashed border-gold/40 bg-gradient-to-br from-cream/60 to-background p-8 text-center sm:p-12">
      <div
        aria-hidden
        className="mx-auto flex h-20 w-20 items-center justify-center rounded-full bg-gradient-to-br from-gold/30 to-cream shadow-inner"
      >
        <Mail className="h-10 w-10 text-[var(--gold-deep)]" />
      </div>
      <h3 className="mt-5 font-display text-xl text-foreground sm:text-2xl">
        📧 No campaigns yet
      </h3>
      <p className="mx-auto mt-2 max-w-md text-sm leading-relaxed text-foreground/75">
        Create your first email campaign to keep your community informed about Recovery Trauma™.
      </p>
      <Link
        to="/admin/broadcasts/$id"
        params={{ id: "new" }}
        className="mt-6 inline-flex items-center justify-center gap-2 rounded-2xl bg-gradient-to-r from-[var(--gold-deep)] to-gold px-6 py-3 text-base font-semibold text-cream shadow-[var(--shadow-gold)] transition-all duration-200 hover:-translate-y-0.5 hover:shadow-lg active:scale-[0.99]"
      >
        <Sparkles className="h-5 w-5" /> Create My First Campaign
      </Link>
    </div>
  );
}

function ComingSoonTile({ icon, label }: { icon: React.ReactNode; label: string }) {
  return (
    <div className="relative flex items-center gap-3 rounded-2xl border border-dashed border-border bg-card/60 p-4 text-sm">
      <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-gold/15 text-[var(--gold-deep)]">
        {icon}
      </span>
      <div className="min-w-0 flex-1">
        <div className="truncate font-medium text-foreground">{label}</div>
        <div className="text-[11px] uppercase tracking-wider text-muted-foreground">
          Coming soon
        </div>
      </div>
      <Lock className="h-3.5 w-3.5 text-muted-foreground/60" />
    </div>
  );
}

function SendThrottlePanel() {
  const qc = useQueryClient();
  const getFn = useServerFn(getBroadcastSendSettings);
  const updateFn = useServerFn(updateBroadcastSendSettings);
  const { data, isLoading } = useQuery({
    queryKey: ["broadcast-send-settings"],
    queryFn: () => getFn({}),
  });
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState<null | {
    send_delay_ms: number;
    max_retries: number;
    retry_backoff_base_ms: number;
    retry_backoff_max_ms: number;
    error_backoff_base_ms: number;
  }>(null);
  useEffect(() => {
    if (data && !form) {
      setForm({
        send_delay_ms: data.send_delay_ms,
        max_retries: data.max_retries,
        retry_backoff_base_ms: data.retry_backoff_base_ms,
        retry_backoff_max_ms: data.retry_backoff_max_ms,
        error_backoff_base_ms: data.error_backoff_base_ms,
      });
    }
  }, [data, form]);
  const save = useMutation({
    mutationFn: () => updateFn({ data: form! }),
    onSuccess: () => {
      toast.success("Throttle settings saved");
      qc.invalidateQueries({ queryKey: ["broadcast-send-settings"] });
    },
    onError: (e: Error) => toast.error(e.message || "Save failed"),
  });

  if (isLoading || !form) return null;
  const perSec = form.send_delay_ms > 0 ? (1000 / form.send_delay_ms).toFixed(1) : "∞";

  return (
    <section className="mt-6 rounded-2xl border border-border bg-card p-4">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-center justify-between text-start"
      >
        <div>
          <div className="flex items-center gap-2 text-sm font-semibold text-foreground">
            <Send className="h-4 w-4 text-[var(--gold-deep)]" />
            Send throttle & retry backoff
          </div>
          <div className="mt-1 text-xs text-muted-foreground">
            {form.send_delay_ms}ms between sends (~{perSec}/sec) · {form.max_retries} retries ·
            429 backoff {form.retry_backoff_base_ms}–{form.retry_backoff_max_ms}ms
          </div>
        </div>
        <span className="text-xs text-muted-foreground">{open ? "Hide" : "Edit"}</span>
      </button>

      {open && (
        <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
          <ThrottleField
            label="Delay between sends (ms)"
            hint="Higher = slower & safer. Resend caps at 10/sec."
            min={0}
            max={10000}
            value={form.send_delay_ms}
            onChange={(v) => setForm({ ...form, send_delay_ms: v })}
          />
          <ThrottleField
            label="Max retries per recipient"
            hint="Total attempts before marking failed."
            min={1}
            max={10}
            value={form.max_retries}
            onChange={(v) => setForm({ ...form, max_retries: v })}
          />
          <ThrottleField
            label="429 backoff base (ms)"
            hint="First retry waits this long; then 2×, 3× …"
            min={100}
            max={60000}
            value={form.retry_backoff_base_ms}
            onChange={(v) => setForm({ ...form, retry_backoff_base_ms: v })}
          />
          <ThrottleField
            label="429 backoff max (ms)"
            hint="Cap on backoff wait per retry."
            min={100}
            max={120000}
            value={form.retry_backoff_max_ms}
            onChange={(v) => setForm({ ...form, retry_backoff_max_ms: v })}
          />
          <ThrottleField
            label="Non-429 error backoff base (ms)"
            hint="Network / 5xx retry wait; scaled per attempt."
            min={100}
            max={60000}
            value={form.error_backoff_base_ms}
            onChange={(v) => setForm({ ...form, error_backoff_base_ms: v })}
          />
          <div className="flex items-end">
            <button
              onClick={() => save.mutate()}
              disabled={save.isPending}
              className="w-full rounded-lg bg-[var(--gold-deep)] px-3 py-2 text-sm font-semibold text-white hover:bg-[var(--gold-deep)]/90 disabled:opacity-50"
            >
              {save.isPending ? "Saving…" : "Save settings"}
            </button>
          </div>
        </div>
      )}
    </section>
  );
}

function ThrottleField({
  label,
  hint,
  min,
  max,
  value,
  onChange,
}: {
  label: string;
  hint: string;
  min: number;
  max: number;
  value: number;
  onChange: (v: number) => void;
}) {
  return (
    <label className="block">
      <div className="text-xs font-medium text-foreground">{label}</div>
      <input
        type="number"
        min={min}
        max={max}
        value={value}
        onChange={(e) => {
          const n = Number(e.target.value);
          if (Number.isFinite(n)) onChange(Math.max(min, Math.min(max, Math.round(n))));
        }}
        className="mt-1 w-full rounded-lg border border-border bg-background px-3 py-2 text-sm"
      />
      <div className="mt-1 text-[11px] text-muted-foreground">{hint}</div>
    </label>
  );
}
